import { useEffect, useRef } from 'react';
import type { CSSProperties } from 'react';
import {
  Alert,
  Button,
  Col,
  Form,
  Input,
  InputNumber,
  Row,
  Select,
  Space,
  Typography,
  theme,
} from 'antd';
import type { FormInstance } from 'antd';
import type { CobroExcepcionResponse, CobroClienteResponse } from '@aid/shared';
import { useCliente, useClienteCobros } from '../../clientes/api/useClientes';
import { formatCurrency } from '../../../lib/format';
import { formatCompactDate, formatearPeriodo } from '../../../lib/date';
import { i18nInstance, useT } from '../../../locales';
import { ClienteSelect } from '../../../components/ClienteSelect';

const { Text } = Typography;

const itemStyle: CSSProperties = { marginBottom: 20 };

function getPendingBalance(cobro: CobroClienteResponse): number | null {
  const pendingBalance = Number(cobro.balance?.saldoPendiente);
  return Number.isFinite(pendingBalance) && pendingBalance > 0 ? pendingBalance : null;
}

function isEligibleInvoice(cobro: CobroClienteResponse): boolean {
  return cobro.estado?.key === 'VIGENTE' && getPendingBalance(cobro) !== null;
}

function invoiceLabel(cobro: CobroClienteResponse, t: ReturnType<typeof useT>): string {
  const date =
    formatearPeriodo(cobro.fechaInicio, cobro.fechaFin) ??
    formatCompactDate(
      cobro.fechaInicio ?? cobro.fechaFin ?? cobro.fechaCobro,
      i18nInstance.language,
    );

  return [`${t('pages.cobros.factura')} #${cobro.id}`, date, formatCurrency(cobro.valor)]
    .filter((part): part is string => Boolean(part))
    .join(' · ');
}

interface ExcepcionFormProps {
  mode: 'create' | 'edit';
  initialValues?: CobroExcepcionResponse | null;
  onSubmit: (values: Record<string, unknown>) => void;
  loading?: boolean;
  preselectedClienteId?: number;
  form?: FormInstance;
  hideSubmit?: boolean;
}

interface InvoiceFieldsProps {
  form: FormInstance;
  t: ReturnType<typeof useT>;
}

interface AmountFieldProps {
  mode: 'create' | 'edit';
  t: ReturnType<typeof useT>;
  maxPendingBalance?: number | null;
}

/**
 * Contexto read-only del cliente cuando la excepción se crea desde Cliente 360
 * (cliente preseleccionado). No es un campo editable: el cliente es contextual
 * y no debe parecer un Select deshabilitado. El nombre se resuelve con
 * useCliente, que comparte la caché del detalle del cliente ya cargado.
 */
function ClienteContextoReadOnly({ clienteId }: { clienteId: number }) {
  const t = useT();
  const { data: cliente, isLoading } = useCliente(clienteId);
  const secondary = cliente
    ? [cliente.ip, cliente.identificacion].filter(Boolean).join(' · ')
    : null;

  return (
    <div style={{ marginBottom: 20 }}>
      <Text
        style={{
          display: 'block',
          marginBottom: 8,
          fontSize: 14,
          lineHeight: '22px',
        }}
      >
        {t('pages.excepciones.cliente')}
      </Text>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, minHeight: 24 }}>
        <Text strong style={{ fontSize: 14 }}>
          {isLoading || !cliente ? `#${clienteId}` : (cliente.nombre ?? `#${clienteId}`)}
        </Text>
        {!isLoading && secondary && (
          <Text type="secondary" style={{ fontSize: 13 }}>
            {secondary}
          </Text>
        )}
      </div>
    </div>
  );
}

/**
 * Resumen financiero de la factura seleccionada: valor total y saldo pendiente.
 * El monto de la excepción queda explícitamente acotado por ese saldo (el
 * campo de monto aplica max + validación sobre `saldoPendiente`).
 */
function FacturaResumen({ cobro, t }: { cobro: CobroClienteResponse; t: ReturnType<typeof useT> }) {
  const { token } = theme.useToken();
  const date =
    formatearPeriodo(cobro.fechaInicio, cobro.fechaFin) ??
    formatCompactDate(
      cobro.fechaInicio ?? cobro.fechaFin ?? cobro.fechaCobro,
      i18nInstance.language,
    );
  const pendingBalance = getPendingBalance(cobro);

  return (
    <div
      style={{
        marginTop: 8,
        // Separación clara con el label de Monto que sigue (token.margin = 16).
        marginBottom: token.margin,
        padding: '10px 12px',
        borderRadius: 8,
        background: token.colorFillTertiary,
        border: `1px solid ${token.colorBorderSecondary}`,
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap' }}>
        <Text strong style={{ fontSize: 13 }}>
          {t('pages.cobros.factura')} #{cobro.id}
          {date && (
            <Text type="secondary" style={{ fontSize: 13, fontWeight: 400 }}>
              {' '}
              · {date}
            </Text>
          )}
        </Text>
        {cobro.estado?.value && (
          <Text type="secondary" style={{ fontSize: 12 }}>
            {cobro.estado.value}
          </Text>
        )}
      </div>
      <div style={{ display: 'flex', gap: 24, marginTop: 8, flexWrap: 'wrap' }}>
        <Text style={{ fontSize: 13 }}>
          <Text type="secondary" style={{ fontSize: 12 }}>
            {t('pages.excepciones.facturaValor')}:{' '}
          </Text>
          <Text strong style={{ fontVariantNumeric: 'tabular-nums' }}>
            {formatCurrency(cobro.valor)}
          </Text>
        </Text>
        {pendingBalance !== null && (
          <Text style={{ fontSize: 13 }}>
            <Text type="secondary" style={{ fontSize: 12 }}>
              {t('pages.excepciones.facturaSaldoPendiente')}:{' '}
            </Text>
            <Text strong style={{ fontVariantNumeric: 'tabular-nums' }}>
              {formatCurrency(pendingBalance.toFixed(2))}
            </Text>
          </Text>
        )}
      </div>
    </div>
  );
}

function AmountField({ mode, t, maxPendingBalance = null }: AmountFieldProps) {
  const isCreate = mode === 'create';
  const maximumMessage =
    maxPendingBalance !== null
      ? t('pages.excepciones.montoMaximo', {
          amount: formatCurrency(maxPendingBalance.toFixed(2)),
        })
      : undefined;

  return (
    <Col span={24}>
      <Form.Item
        style={itemStyle}
        name={isCreate ? 'monto' : 'valor'}
        label={t(isCreate ? 'pages.excepciones.monto' : 'pages.excepciones.valor')}
        extra={isCreate ? maximumMessage : t('pages.excepciones.montoInmutable')}
        rules={
          isCreate
            ? [
                { required: true, message: t('pages.excepciones.montoRequired') },
                {
                  type: 'number',
                  min: 0.01,
                  message: t('pages.excepciones.montoPositive'),
                },
                ...(maxPendingBalance !== null
                  ? [
                      {
                        type: 'number' as const,
                        max: maxPendingBalance,
                        message: maximumMessage,
                      },
                    ]
                  : []),
              ]
            : undefined
        }
      >
        <InputNumber
          min={isCreate ? 0.01 : 0}
          max={isCreate ? (maxPendingBalance ?? undefined) : undefined}
          step={0.01}
          precision={2}
          disabled={!isCreate}
          style={{ width: '100%' }}
        />
      </Form.Item>
    </Col>
  );
}

function InvoiceFields({ form, t }: InvoiceFieldsProps) {
  const selectedClienteIdValue = Form.useWatch('idCliente', form) as number | null | undefined;
  const selectedClienteId =
    typeof selectedClienteIdValue === 'number' && selectedClienteIdValue > 0
      ? selectedClienteIdValue
      : 0;
  const cobrosQuery = useClienteCobros(selectedClienteId, { limit: 100, offset: 0 });
  const cobros = cobrosQuery.data?.data ?? [];
  const eligibleCobros = cobros.filter(isEligibleInvoice);
  const invoiceOptions = eligibleCobros.map((cobro) => ({
    value: cobro.id,
    label: invoiceLabel(cobro, t),
  }));
  const selectedCobroIdValue = Form.useWatch('idCobro', form) as number | null | undefined;
  const selectedCobro = eligibleCobros.find((cobro) => cobro.id === selectedCobroIdValue);
  const selectedPendingBalance = selectedCobro ? getPendingBalance(selectedCobro) : null;
  const previousClienteIdRef = useRef<number | undefined>(selectedClienteIdValue ?? undefined);

  useEffect(() => {
    const currentClienteId = selectedClienteIdValue ?? undefined;
    if (previousClienteIdRef.current !== currentClienteId) {
      form.setFieldValue('idCobro', undefined);
    }
    previousClienteIdRef.current = currentClienteId;
  }, [form, selectedClienteIdValue]);

  const invoiceStatus = !selectedClienteId ? (
    <Alert type="info" showIcon message={t('pages.excepciones.facturaNoCliente')} />
  ) : cobrosQuery.isLoading ? (
    <Alert type="info" showIcon message={t('pages.excepciones.facturaLoading')} />
  ) : cobrosQuery.error ? (
    <Alert
      type="error"
      showIcon
      message={t('pages.excepciones.facturaError')}
      action={
        <Button size="small" onClick={() => void cobrosQuery.refetch()}>
          {t('common.retry')}
        </Button>
      }
    />
  ) : cobros.length === 0 ? (
    <Alert type="warning" showIcon message={t('pages.excepciones.facturaEmpty')} />
  ) : eligibleCobros.length === 0 ? (
    <Alert type="warning" showIcon message={t('pages.excepciones.facturaNoElegibles')} />
  ) : null;

  return (
    <>
      <Col span={24}>
        <Form.Item
          style={itemStyle}
          name="idCobro"
          label={t('pages.excepciones.factura')}
          rules={[{ required: true, message: t('pages.excepciones.facturaRequired') }]}
        >
          <Select
            showSearch
            optionFilterProp="label"
            options={invoiceOptions}
            loading={cobrosQuery.isLoading}
            disabled={
              !selectedClienteId ||
              cobrosQuery.isLoading ||
              Boolean(cobrosQuery.error) ||
              eligibleCobros.length === 0
            }
            notFoundContent={t('common.noResults')}
            placeholder={t('pages.excepciones.facturaPlaceholder')}
            style={{ width: '100%' }}
          />
        </Form.Item>
        {invoiceStatus}
        {selectedCobro && <FacturaResumen cobro={selectedCobro} t={t} />}
      </Col>
      <AmountField mode="create" t={t} maxPendingBalance={selectedPendingBalance} />
    </>
  );
}

export function ExcepcionForm({
  mode,
  initialValues,
  onSubmit,
  loading,
  preselectedClienteId,
  form: externalForm,
  hideSubmit = false,
}: ExcepcionFormProps) {
  const t = useT();
  const [internalForm] = Form.useForm();
  const form = externalForm ?? internalForm;

  useEffect(() => {
    if (initialValues) {
      form.setFieldsValue({
        idCliente: initialValues.idCliente,
        valor: initialValues.monto ?? initialValues.valor ?? '',
        motivo: initialValues.motivo,
      });
    } else if (mode === 'create' && preselectedClienteId) {
      form.setFieldValue('idCliente', preselectedClienteId);
    }
  }, [initialValues, form, mode, preselectedClienteId]);

  const handleFinish = (values: Record<string, unknown>) => {
    const payload: Record<string, unknown> = { ...values };
    if (mode === 'create') {
      delete payload.valor;
      delete payload.periodoInicio;
      delete payload.periodoFin;
      if (payload.monto !== undefined && payload.monto !== null && payload.monto !== '') {
        const monto = Number(payload.monto);
        if (Number.isFinite(monto)) {
          payload.monto = monto.toFixed(2);
        } else {
          delete payload.monto;
        }
      } else {
        delete payload.monto;
      }
    } else {
      if (payload.valor !== undefined && payload.valor !== '') {
        payload.valor = Number(payload.valor).toFixed(2);
      } else {
        delete payload.valor;
      }
    }
    onSubmit(payload);
  };

  return (
    <Form form={form} layout="vertical" onFinish={handleFinish} style={{ width: '100%' }}>
      <Row gutter={16}>
        {mode === 'create' && (
          <>
            <Col span={24}>
              {preselectedClienteId ? (
                <>
                  <ClienteContextoReadOnly clienteId={preselectedClienteId} />
                  {/* Campo registrado pero oculto: mantiene idCliente en el
                      payload y notifica a Form.useWatch (InvoiceFields) sin
                      pintar un Select deshabilitado. */}
                  <Form.Item name="idCliente" hidden>
                    <Input />
                  </Form.Item>
                </>
              ) : (
                <Form.Item
                  style={itemStyle}
                  name="idCliente"
                  label={t('pages.excepciones.cliente')}
                  rules={[{ required: true, message: t('pages.excepciones.clienteRequired') }]}
                >
                  <ClienteSelect />
                </Form.Item>
              )}
            </Col>
            <InvoiceFields form={form} t={t} />
          </>
        )}
        {mode === 'edit' && <AmountField mode="edit" t={t} />}
        <Col span={24}>
          <Form.Item
            style={itemStyle}
            name="motivo"
            label={t('pages.excepciones.motivo')}
            rules={[{ required: true, message: t('pages.excepciones.motivoRequired') }]}
          >
            <Input.TextArea
              rows={3}
              maxLength={500}
              showCount
              placeholder={t('pages.excepciones.motivoPlaceholder')}
            />
          </Form.Item>
        </Col>
        <Col span={24}>
          <Form.Item style={itemStyle}>
            {!hideSubmit && (
              <Space wrap size={[8, 8]}>
                <Button type="primary" htmlType="submit" loading={loading}>
                  {mode === 'create' ? t('pages.excepciones.create') : t('common.saveChanges')}
                </Button>
              </Space>
            )}
          </Form.Item>
        </Col>
      </Row>
    </Form>
  );
}
